import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader,
  AlertCircle,
  CheckCircle,
  Search,
  X,
  Eye,
  Send,
  Clock,
  Phone,
  Users,
  Info,
  ChevronLeft,
  ChevronRight,
  Upload,
  Image as ImageIcon,
  Video,
  File,
} from "lucide-react";
import moment from "moment-timezone";
import WhatsAppPreview from "../components/WhatsAppNewPreview";
import { prepareRetargeting, retargetCampaign, getCampaignById } from "../apis/campaign";
import { getTemplates } from "../apis/template";
import { toast } from "react-toastify";
import api from "../services/api";

// Meta's per-format limits for media template headers. Keep these in sync
// with server/utils/campaignHeaderMedia.js — both client + server enforce them.
const META_HEADER_MEDIA_LIMITS = {
  IMAGE: {
    label: "Image",
    maxBytes: 5 * 1024 * 1024,
    accept: ".jpg,.jpeg,.png,image/jpeg,image/png",
    extensions: [".jpg", ".jpeg", ".png"],
    mimeTypes: ["image/jpeg", "image/png"],
    icon: ImageIcon,
  },
  VIDEO: {
    label: "Video",
    maxBytes: 16 * 1024 * 1024,
    accept: ".mp4,.3gp,video/mp4,video/3gpp",
    extensions: [".mp4", ".3gp"],
    mimeTypes: ["video/mp4", "video/3gpp"],
    icon: Video,
  },
  DOCUMENT: {
    label: "Document",
    maxBytes: 100 * 1024 * 1024,
    accept:
      ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain",
    extensions: [".pdf", ".doc", ".docx", ".ppt", ".pptx", ".xls", ".xlsx", ".txt"],
    mimeTypes: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/plain",
    ],
    icon: File,
  },
};

const formatBytes = (bytes) => {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))}MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${bytes}B`;
};

const validateHeaderMediaFile = (file, format) => {
  const limits = META_HEADER_MEDIA_LIMITS[format];
  if (!limits) {
    return { valid: false, error: "This template header does not accept uploaded media." };
  }
  const lowerName = (file.name || "").toLowerCase();
  const ext = lowerName.includes(".") ? lowerName.slice(lowerName.lastIndexOf(".")) : "";
  const mimeOk = file.type ? limits.mimeTypes.includes(file.type) : false;
  const extOk = ext ? limits.extensions.includes(ext) : false;
  if (!mimeOk && !extOk) {
    return {
      valid: false,
      error: `${limits.label} header only accepts ${limits.extensions.join(", ")}`,
    };
  }
  if (file.size > limits.maxBytes) {
    return {
      valid: false,
      error: `${limits.label} exceeds Meta's ${formatBytes(limits.maxBytes)} limit (file is ${formatBytes(file.size)})`,
    };
  }
  return { valid: true, error: null };
};

// Inspect the template's HEADER format (IMAGE / VIDEO / DOCUMENT / TEXT / NONE).
const getTemplateHeaderFormat = (template) => {
  if (!template) return "NONE";
  let json = template.template_json || {};
  if (typeof json === "string") {
    try {
      json = JSON.parse(json);
    } catch {
      json = {};
    }
  }
  const components = json.components || [];
  const headerComp = components.find((c) => c.type === "HEADER");
  if (headerComp?.format) return String(headerComp.format).toUpperCase();
  if (template.headerFormat) return String(template.headerFormat).toUpperCase();
  if (template.headerType) return String(template.headerType).toUpperCase();
  return "NONE";
};

const RetargetCampaign = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contactFilter = searchParams.get("filter") || "all";

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [phoneAccounts, setPhoneAccounts] = useState([]);
  const [selectedPhoneId, setSelectedPhoneId] = useState("");
  const [validTemplates, setValidTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedTemplateForPreview, setSelectedTemplateForPreview] = useState(null);
  const [originalTemplate, setOriginalTemplate] = useState(null);
  const [campaignName, setCampaignName] = useState("");
  const [availableVariables, setAvailableVariables] = useState([]);
  const [sendOption, setSendOption] = useState("now");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const [defaultValues, setDefaultValues] = useState({});

  // Header media override (image/video/document). When provided, this URL
  // overrides any inherited per-contact value for every contact in the
  // retargeted campaign — same behaviour as Create Campaign.
  const [headerMediaFile, setHeaderMediaFile] = useState(null);
  const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState("");
  const [headerMediaError, setHeaderMediaError] = useState("");
  const headerMediaInputRef = useRef(null);

  const carouselRef = useRef(null);
  const isUpdatingPreview = useRef(false);

  const filterLabels = {
    all: "All Contacts",
    pending: "Pending Contacts",
    sent: "Sent Contacts",
    delivered: "Delivered Contacts",
    read: "Read Contacts",
    failed: "Failed Contacts",
    replies: "Replied Contacts",
  };

  // Load campaign + phone accounts in parallel for fast open
  useEffect(() => {
    if (!campaignId || campaignId === "null" || campaignId === "undefined") {
      toast.error("Invalid campaign ID");
      navigate("/campaigns");
      return;
    }
    let cancelled = false;
    setLoadingData(true);
    const run = async () => {
      try {
        const [campaignRes, accountsRes] = await Promise.all([
          getCampaignById(campaignId),
          api.get("/api/whatsapp-accounts", { params: { section: "campaigns" } }),
        ]);
        if (cancelled) return;
        if (campaignRes.data?.success && campaignRes.data?.data) {
          const c = campaignRes.data.data;
          setCampaign(c);
          setCampaignName(`Retarget - ${c.campaign_name}`);
          if (c.phone_id) setSelectedPhoneId(c.phone_id);
        } else {
          toast.error("Failed to load campaign data");
          navigate("/campaigns");
          return;
        }
        if (accountsRes.data?.success && Array.isArray(accountsRes.data?.data)) {
          const flattened = [];
          accountsRes.data.data.forEach((account) => {
            if (account.phone_numbers && Array.isArray(account.phone_numbers) && account.phone_numbers.length > 0) {
              account.phone_numbers.forEach((phone) => {
                flattened.push({
                  id: account.id || account.waba_id,
                  waba_id: account.waba_id || account.id,
                  phone_id: phone.phone_id,
                  phone_number: phone.phone_number,
                  verified_name: phone.verified_name || phone.phone_name || "",
                });
              });
            } else if (account.phone_id) {
              flattened.push({
                id: account.waba_id || account.id,
                waba_id: account.waba_id || account.id,
                phone_id: account.phone_id,
                phone_number: account.phone_number,
                verified_name: account.verified_name || account.phone_name || "",
              });
            }
          });
          setPhoneAccounts(flattened);
        } else {
          setPhoneAccounts([]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading retarget data:", error);
          toast.error(error.response?.data?.error || "Failed to load data");
          navigate("/campaigns");
        }
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [campaignId]);

  useEffect(() => {
    if (!selectedPhoneId) {
      setValidTemplates([]);
      setSelectedTemplates([]);
      setSelectedTemplateForPreview(null);
      setAvailableVariables([]);
      return;
    }
    loadValidTemplates();
  }, [selectedPhoneId]);

  // Update preview when templates or valid templates change
  useEffect(() => {
    updatePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplates, validTemplates]);

  // The header media UI is driven by the FIRST selected template that has a
  // media header (IMAGE / VIDEO / DOCUMENT). This way the upload still works
  // when the user is previewing a different template at the moment.
  const mediaHeaderTemplate = (() => {
    for (const id of selectedTemplates) {
      const t = validTemplates.find(
        (tpl) => String(tpl.id).trim() === String(id).trim()
      );
      if (!t) continue;
      const fmt = getTemplateHeaderFormat(t);
      if (["IMAGE", "VIDEO", "DOCUMENT"].includes(fmt)) {
        return { template: t, format: fmt };
      }
    }
    return null;
  })();
  const headerMediaFormat = mediaHeaderTemplate?.format || "NONE";
  const headerMediaLimits = META_HEADER_MEDIA_LIMITS[headerMediaFormat] || null;

  // Drop any selected file the moment no media-header template is active any
  // more (e.g. user removed all IMAGE templates from the selection).
  useEffect(() => {
    if (!headerMediaLimits) {
      if (headerMediaFile) setHeaderMediaFile(null);
      if (headerMediaPreviewUrl) {
        URL.revokeObjectURL(headerMediaPreviewUrl);
        setHeaderMediaPreviewUrl("");
      }
      if (headerMediaError) setHeaderMediaError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerMediaFormat]);

  // Clean up the preview object URL on unmount.
  useEffect(() => {
    return () => {
      if (headerMediaPreviewUrl) URL.revokeObjectURL(headerMediaPreviewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHeaderMediaChange = (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;

    if (!headerMediaLimits) {
      setHeaderMediaError(
        "None of the selected templates have an image/video/document header."
      );
      return;
    }

    const result = validateHeaderMediaFile(file, headerMediaFormat);
    if (!result.valid) {
      setHeaderMediaError(result.error);
      return;
    }

    if (headerMediaPreviewUrl) URL.revokeObjectURL(headerMediaPreviewUrl);
    const previewUrl = URL.createObjectURL(file);
    setHeaderMediaFile(file);
    setHeaderMediaPreviewUrl(previewUrl);
    setHeaderMediaError("");
  };

  const handleRemoveHeaderMedia = () => {
    if (headerMediaPreviewUrl) URL.revokeObjectURL(headerMediaPreviewUrl);
    setHeaderMediaFile(null);
    setHeaderMediaPreviewUrl("");
    setHeaderMediaError("");
    if (headerMediaInputRef.current) {
      headerMediaInputRef.current.value = "";
    }
  };

  // Update preview when activePreviewIndex changes (but only if templates are selected)
  // Use a ref to prevent infinite loops when updatePreview adjusts the index
  useEffect(() => {
    if (selectedTemplates.length > 0 && !isUpdatingPreview.current) {
      isUpdatingPreview.current = true;
      updatePreview();
      // Reset flag after update
      setTimeout(() => {
        isUpdatingPreview.current = false;
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePreviewIndex]);

  const loadValidTemplates = async () => {
    if (!selectedPhoneId) {
      setValidTemplates([]);
      return;
    }

    const phoneIdNum = typeof selectedPhoneId === 'string' ? parseInt(selectedPhoneId, 10) : selectedPhoneId;
    if (isNaN(phoneIdNum) || phoneIdNum <= 0) {
      toast.error("Invalid phone number selected");
      setValidTemplates([]);
      return;
    }

    setLoadingTemplates(true);
    try {
      // Fetch templates and prepareRetargeting in parallel for faster load
      const templatesPromise = getTemplates({
        phoneId: phoneIdNum,
        status: "approved",
        limit: 1000,
        offset: 0,
      });
      const retargetPromise = campaign && campaignId
        ? prepareRetargeting(campaignId, selectedPhoneId)
        : Promise.resolve({ data: { success: false } });

      const [templatesResponse, retargetResponse] = await Promise.all([
        templatesPromise,
        retargetPromise,
      ]);

      let allTemplates = [];
      if (templatesResponse.data.success) {
        const rawTemplates = templatesResponse.data.metaTemplates || templatesResponse.data.templates || [];
        allTemplates = rawTemplates.filter(
          (t) =>
            (t.status === "approved" || t.status === "APPROVED") &&
            (t.category || "").toLowerCase() !== "authentication"
        );
      }

      if (campaign && allTemplates.length > 0 && retargetResponse.data?.success) {
        setAvailableVariables(retargetResponse.data.data.available_variables || []);
        const validTemplateIds = (retargetResponse.data.data.valid_templates || []).map(t => t.id);
        const filtered = allTemplates.filter(t => validTemplateIds.includes(t.id));
        setValidTemplates(filtered.length > 0 ? filtered : allTemplates);
      } else if (retargetResponse.data?.success === false && campaign) {
        setValidTemplates(allTemplates);
        setAvailableVariables([]);
      } else {
        setValidTemplates(allTemplates);
        setAvailableVariables(retargetResponse.data?.data?.available_variables ?? []);
      }

      setSelectedTemplates([]);
      setSelectedTemplateForPreview(null);
      setOriginalTemplate(null);
      setDefaultValues({});

      if (allTemplates.length === 0) {
        toast.warning("No approved templates found for this phone number");
      }
    } catch (error) {
      console.error("Error loading templates:", error);
      toast.error(error.response?.data?.error || "Failed to load templates");
      setValidTemplates([]);
      setAvailableVariables([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateToggle = (templateId) => {
    setSelectedTemplates((prev) => {
      if (prev.includes(templateId)) {
        const newList = prev.filter((id) => id !== templateId);
        // Adjust activePreviewIndex if needed
        const removedIndex = prev.indexOf(templateId);
        if (removedIndex !== -1) {
          if (activePreviewIndex >= newList.length && newList.length > 0) {
            setActivePreviewIndex(newList.length - 1);
          } else if (removedIndex < activePreviewIndex) {
            setActivePreviewIndex(Math.max(0, activePreviewIndex - 1));
          }
        }
        return newList;
      } else {
        return [...prev, templateId];
      }
    });
  };

  const extractRequiredParameters = (template) => {
    if (!template) return [];

    const parameters = [];
    let templateJson = template.template_json || {};
    if (typeof templateJson === "string") {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        templateJson = {};
      }
    }

    const components = templateJson.components || [];

    const headerComponent = components.find((c) => c.type === "HEADER");
    if (headerComponent) {
      const headerFormat = headerComponent.format?.toUpperCase() || "NONE";
      const headerText = headerComponent.text || "";

      if (headerFormat === "TEXT") {
        const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
        headerVarMatches.forEach((match) => {
          const varNum = match.replace(/\{\{|\}\}/g, "");
          parameters.push({
            key: `header_${varNum}`,
            component: "HEADER",
            varNum,
            placeholder: match,
            label: `Header Variable ${varNum}`,
          });
        });
      }
    }

    const bodyComponent = components.find((c) => c.type === "BODY");
    const bodyText = bodyComponent?.text || "";
    const bodyVarMatches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
    bodyVarMatches.forEach((match) => {
      const varNum = match.replace(/\{\{|\}\}/g, "");
      parameters.push({
        key: `body_${varNum}`,
        component: "BODY",
        varNum,
        placeholder: match,
        label: `Body Variable ${varNum}`,
      });
    });

    const buttonsComponent = components.find((c) => c.type === "BUTTONS");
    const buttons = buttonsComponent?.buttons || [];
    buttons.forEach((btn, index) => {
      if (btn.type === "URL" && btn.url && btn.url.includes("{{")) {
        parameters.push({
          key: `button_text_${index}`,
          component: "BUTTON",
          index,
          label: `Button ${index + 1} Text`,
        });
      }
    });

    return parameters;
  };

  const transformTemplateForPreview = (template) => {
    if (!template) return null;
    
    // If template already has body and no template_json, return as-is with defaults
    if (template.body && !template.template_json) {
      return {
        ...template,
        headerFormat: template.headerFormat || template.headerType || "NONE",
        buttons: template.buttons || [],
        variables: template.variables || [],
      };
    }
    
    let templateJson = template.template_json || {};
    if (typeof templateJson === 'string') {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        templateJson = {};
      }
    }
    
    const components = templateJson.components || [];
    
    // If no components, return template with defaults
    if (components.length === 0) {
      return {
        ...template,
        body: template.body || '',
        footer: template.footer || '',
        headerFormat: template.headerFormat || template.headerType || "NONE",
        headerText: template.headerText || '',
        header_media_url: template.header_media_url || null,
        buttons: template.buttons || [],
        variables: template.variables || [],
      };
    }
    
    const bodyComponent = components.find(c => c.type === 'BODY');
    const body = bodyComponent?.text || template.body || '';
    
    const footerComponent = components.find(c => c.type === 'FOOTER');
    const footer = footerComponent?.text || template.footer || '';
    
    const headerComponent = components.find(c => c.type === 'HEADER');
    const headerFormat = headerComponent?.format?.toUpperCase() || 
                        template.headerFormat?.toUpperCase() || 
                        template.headerType?.toUpperCase() || 
                        'NONE';
    const headerText = headerComponent?.text || template.headerText || '';
    const headerMediaUrl = template.header_media_url || null;
    
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    const rawButtons = buttonsComponent?.buttons || template.buttons || [];
    const buttons = rawButtons.map((btn, index) => ({
      id: index,
      type: btn.type,
      text: btn.text || btn.url || btn.phone_number || `Button ${index + 1}`,
      url: btn.url,
      phone_number: btn.phone_number,
    }));
    
    // Extract header variables with sample values
    const headerVariables = [];
    if (headerComponent && headerFormat === 'TEXT' && headerText) {
      const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
      headerVarMatches.forEach((match, index) => {
        const varNum = match.replace(/\{\{|\}\}/g, '');
        const varIndex = parseInt(varNum, 10) - 1;
        const sampleValue = headerComponent?.example?.header_text?.[0]?.[varIndex] || 
                           headerComponent?.example?.header_handle?.[0] || 
                           `Header Sample ${varNum}`;
        headerVariables.push({
          id: `header_${index}`,
          name: varNum,
          sample: sampleValue,
          component: 'HEADER',
        });
      });
    }

    // Extract body variables with sample values
    const bodyVariableMatches = body.match(/\{\{(\d+)\}\}/g) || [];
    const bodyVariables = bodyVariableMatches.map((match, index) => {
      const varNum = match.replace(/\{\{|\}\}/g, '');
      // body_text is an array of arrays, so we need body_text[0][varNum - 1]
      // varNum is a string, so we need to convert to number and subtract 1 for 0-based indexing
      const varIndex = parseInt(varNum, 10) - 1;
      const sampleValue = bodyComponent?.example?.body_text?.[0]?.[varIndex] || 
                         `Body Sample ${varNum}`;
      return {
        id: `body_${index}`,
        name: varNum,
        sample: sampleValue,
        component: 'BODY',
      };
    });

    // Combine all variables
    const variables = [...headerVariables, ...bodyVariables];
    
    return {
      ...template,
      body,
      footer,
      headerFormat,
      headerText,
      header_media_url: headerMediaUrl,
      buttons,
      variables,
    };
  };

  const navigatePreview = (direction) => {
    setActivePreviewIndex((prev) => {
      if (direction === 'next') {
        return prev < selectedTemplates.length - 1 ? prev + 1 : 0;
      } else {
        return prev > 0 ? prev - 1 : selectedTemplates.length - 1;
      }
    });
  };

  const updatePreview = () => {
    if (selectedTemplates.length === 0) {
      setSelectedTemplateForPreview(null);
      setOriginalTemplate(null);
      setDefaultValues({});
      setActivePreviewIndex(0);
      return;
    }

    // Ensure activePreviewIndex is within bounds
    if (activePreviewIndex >= selectedTemplates.length && selectedTemplates.length > 0) {
      setActivePreviewIndex(0);
      return; // Return early, will be called again with corrected index
    }

    const selectedTemplateObjects = validTemplates.filter((t) =>
      selectedTemplates.includes(t.id)
    );

    if (selectedTemplateObjects.length > 0) {
      const currentTemplate = selectedTemplateObjects[activePreviewIndex] || selectedTemplateObjects[0];
      const transformedTemplate = transformTemplateForPreview(currentTemplate);
      if (transformedTemplate) {
        transformedTemplate.selectedTemplates = selectedTemplateObjects.map((t) => ({
          id: t.id,
          name: t.name,
          category: t.category,
        }));
        setSelectedTemplateForPreview(transformedTemplate);
        setOriginalTemplate(currentTemplate);

        const requiredParams = extractRequiredParameters(currentTemplate);
        const newDefaults = {};
        requiredParams.forEach((param) => {
          // Set to empty string - WhatsAppNewPreview will use sample values from variables array
          newDefaults[param.key] = '';
        });
        // Only update defaultValues if they've actually changed to avoid infinite loop
        setDefaultValues(prev => {
          const prevKeys = Object.keys(prev).sort().join(',');
          const newKeys = Object.keys(newDefaults).sort().join(',');
          if (prevKeys !== newKeys) {
            return newDefaults;
          }
          // Check if values changed
          const valuesChanged = Object.keys(newDefaults).some(key => prev[key] !== newDefaults[key]);
          return valuesChanged ? newDefaults : prev;
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (!selectedPhoneId) {
      toast.error("Please select a phone number");
      return;
    }

    if (selectedTemplates.length === 0) {
      toast.error("Please select at least one template");
      return;
    }

    if (!campaignName.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }

    if (sendOption === "schedule") {
      if (!scheduledDate || !scheduledTime) {
        toast.error("Please select scheduled date and time");
        return;
      }
    }

    try {
      setLoading(true);
      
      // Validate campaignId
      if (!campaignId || campaignId === 'null' || campaignId === 'undefined') {
        toast.error("Invalid campaign ID");
        console.error("[Retarget] Invalid campaignId:", campaignId);
        return;
      }

      const selectedPhoneAccount = phoneAccounts.find(
        (acc) => acc.phone_id === selectedPhoneId
      );
      if (!selectedPhoneAccount) {
        toast.error("Selected phone account not found");
        return;
      }

      // Convert phone_id to number
      const phoneIdNum = typeof selectedPhoneId === 'string' ? parseInt(selectedPhoneId, 10) : selectedPhoneId;
      if (isNaN(phoneIdNum) || phoneIdNum <= 0) {
        toast.error("Invalid phone number selected");
        return;
      }

      // Convert template_ids to numbers
      const templateIds = selectedTemplates.map(id => {
        const numId = typeof id === 'string' ? parseInt(id, 10) : id;
        return isNaN(numId) ? null : numId;
      }).filter(id => id !== null);

      if (templateIds.length === 0) {
        toast.error("Please select at least one template");
        return;
      }

      const retargetData = {
        whatsapp_account_id: selectedPhoneAccount.waba_id || selectedPhoneAccount.id,
        phone_id: phoneIdNum,
        template_ids: templateIds,
        campaign_name: campaignName.trim(),
        contact_filter: contactFilter,
        sendOption: sendOption,
      };

      if (sendOption === "schedule") {
        const scheduledTimestamp = `${scheduledDate} ${scheduledTime}:00`;
        retargetData.scheduledTimestamp = scheduledTimestamp;
        retargetData.timezone = timezone;
      }

      // Only attach the upload when the current selection actually accepts it.
      // The retargetCampaign API helper switches to multipart/form-data
      // automatically when this field is present.
      if (headerMediaLimits && headerMediaFile) {
        retargetData.headerMediaFile = headerMediaFile;
      }

      // console.log("[Retarget] Sending retarget request:", {
      //   campaignId,
      //   retargetData
      // });

      // API function doesn't need token - it's handled by interceptor
      const response = await retargetCampaign(campaignId, retargetData);

      if (response.data.success) {
        toast.success(response.data.message || "Campaign retargeted successfully");
        // Small delay to ensure toast is visible before navigation
        setTimeout(() => {
          navigate("/campaigns");
        }, 500);
      } else {
        toast.error(response.data.error || "Failed to retarget campaign");
      }
    } catch (error) {
      console.error("Error retargeting campaign:", error);
      toast.error(error.response?.data?.error || "Failed to retarget campaign");
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = validTemplates.filter((template) =>
    template.name.toLowerCase().includes(templateSearchQuery.toLowerCase())
  );

  if (loadingData && !campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Use replace to avoid adding to history, and force navigation
              navigate("/campaigns", { replace: true });
              // Force a page reload if navigate doesn't work
              setTimeout(() => {
                if (window.location.pathname !== "/campaigns") {
                  window.location.href = "/campaigns";
                }
              }, 100);
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors cursor-pointer"
          >
            <ArrowLeft size={20} />
            <span className="font-semibold">Back to Campaigns</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Retarget Campaign</h1>
          <p className="text-sm text-gray-600 mt-1">
            Create a new campaign using contacts from the original campaign
          </p>
        </div>

        {/* Original Campaign Info */}
        {campaign && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  Original Campaign: {campaign.campaign_name}
                </p>
                <p className="text-xs text-blue-700">
                  Retargeting: <strong>{filterLabels[contactFilter] || contactFilter}</strong>
                </p>
                {availableVariables.length > 0 && (
                  <p className="text-xs text-blue-700 mt-1">
                    Available Variables: <strong>{availableVariables.join(", ")}</strong>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Form */}
          <div className="space-y-6">
            {/* Phone Number Selection */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Phone size={16} />
                Select Phone Number *
              </h2>
              <select
                value={selectedPhoneId}
                onChange={(e) => setSelectedPhoneId(e.target.value)}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                disabled={loadingData}
              >
                <option value="">Select a phone number</option>
                {phoneAccounts.map((account) => (
                  <option key={account.phone_id} value={account.phone_id}>
                    {account.phone_number} {account.verified_name ? `(${account.verified_name})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Template Selection */}
            {selectedPhoneId && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Users size={16} />
                  Select Templates * (Select multiple for shuffling)
                </h2>

                {loadingTemplates && (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-5 h-5 animate-spin text-blue-600" />
                    <span className="ml-2 text-sm text-gray-600">Loading valid templates...</span>
                  </div>
                )}

                {!loadingTemplates && validTemplates.length === 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      No valid templates found for this phone number. Templates must match available variables.
                    </p>
                  </div>
                )}

                {!loadingTemplates && validTemplates.length > 0 && (
                  <>
                    {/* Search */}
                    <div className="mb-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <input
                          type="text"
                          placeholder="Search templates..."
                          value={templateSearchQuery}
                          onChange={(e) => setTemplateSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                        />
                      </div>
                    </div>

                    {/* Selected Templates Chips */}
                    {selectedTemplates.length > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-gray-700">
                            Selected Templates ({selectedTemplates.length})
                          </span>
                          {selectedTemplates.length > 1 && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => navigatePreview('prev')}
                                className="p-1 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
                              >
                                <ChevronLeft size={14} />
                              </button>
                              <button
                                onClick={() => navigatePreview('next')}
                                className="p-1 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
                              >
                                <ChevronRight size={14} />
                              </button>
                            </div>
                          )}
                        </div>

                        <div 
                          ref={carouselRef}
                          className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide"
                          style={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none'
                          }}
                        >
                          {selectedTemplates.map((templateId, index) => {
                            const template = validTemplates.find(
                              t => String(t.id).trim() === String(templateId).trim()
                            );
                            if (!template) return null;

                            return (
                              <div
                                key={templateId}
                                className="flex-shrink-0 group"
                              >
                                <div className="inline-flex items-center gap-2 pl-3 pr-2 py-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-all cursor-pointer"
                                  onClick={() => setActivePreviewIndex(index)}
                                >
                                  <span className="inline-flex items-center justify-center w-5 h-5 bg-emerald-600 text-white text-xs font-bold rounded-full flex-shrink-0">
                                    {index + 1}
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900 max-w-[140px] truncate">
                                    {template.name}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTemplateToggle(templateId);
                                    }}
                                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remove"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1 font-medium">
                          <Info size={12} />
                          Click chip to preview • {selectedTemplates.length > 1 ? 'Templates will be randomly shuffled' : 'Add more to enable shuffle'}
                        </p>
                      </div>
                    )}

                    {/* Template List */}
                    <div>
                      <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-xl p-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {(() => {
                            const filtered = validTemplates.filter((template) => {
                              if (!templateSearchQuery.trim()) return true;
                              const query = templateSearchQuery.toLowerCase();
                              return (
                                template.name?.toLowerCase().includes(query) ||
                                template.category?.toLowerCase().includes(query) ||
                                template.language?.toLowerCase().includes(query)
                              );
                            });

                            if (filtered.length === 0) {
                              return (
                                <p className="col-span-2 text-sm text-gray-500 text-center py-6 font-medium">
                                  {templateSearchQuery
                                    ? `No templates found matching "${templateSearchQuery}"`
                                    : "No valid templates found"}
                                </p>
                              );
                            }

                            return filtered.map((template) => {
                              const isSelected = selectedTemplates.some(
                                id => String(id).trim() === String(template.id).trim()
                              );
                              return (
                                <label
                                  key={template.id}
                                  className={`flex items-start gap-2 p-3 rounded-xl cursor-pointer border transition-all ${
                                    isSelected
                                      ? "bg-emerald-50 border-emerald-300"
                                      : "hover:bg-gray-50 border-gray-200 hover:border-gray-300"
                                  }`}
                                >
                                  <div className="mt-0.5 flex-shrink-0">
                                    {isSelected ? (
                                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                                    ) : (
                                      <div className="w-5 h-5 border-2 border-gray-300 rounded-lg" />
                                    )}
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleTemplateToggle(template.id)}
                                    className="hidden"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-gray-900 truncate">
                                      {template.name}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                      {template.category && (
                                        <span className="text-xs text-gray-600 font-medium">
                                          {template.category}
                                        </span>
                                      )}
                                      {template.language && (
                                        <span className="text-xs text-gray-400 font-medium">
                                          • {template.language.toUpperCase()}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </label>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                    
                    {selectedTemplates.length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-3 font-medium mt-3">
                        <CheckCircle size={14} />
                        <span>
                          <strong>{selectedTemplates.length} template{selectedTemplates.length > 1 ? 's' : ''}</strong> selected
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Header Media Override (Image / Video / Document) — optional.
                Renders only when at least one selected template has a media
                header. The uploaded file overrides any inherited per-contact
                value for the entire retargeted campaign. */}
            {headerMediaLimits && (() => {
              const Icon = headerMediaLimits.icon;
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={16} className="text-emerald-600" />
                    <h2 className="text-sm font-bold text-gray-900">
                      Header {headerMediaLimits.label}{" "}
                      <span className="text-xs font-medium text-gray-500">
                        (optional override)
                      </span>
                    </h2>
                  </div>
                  <p className="text-xs text-gray-600 mb-3 font-medium">
                    Upload a single {headerMediaLimits.label.toLowerCase()} to use as the header for{" "}
                    <strong>every contact</strong> in this retargeted campaign.
                    Leave empty to keep the original campaign's header.
                    {mediaHeaderTemplate?.template?.name && (
                      <>
                        {" "}Applies to template{" "}
                        <strong>{mediaHeaderTemplate.template.name}</strong>.
                      </>
                    )}
                  </p>

                  <div className="text-[11px] text-gray-500 mb-3 flex flex-wrap gap-x-3 gap-y-1 font-medium">
                    <span>
                      <strong>Allowed:</strong>{" "}
                      {headerMediaLimits.extensions.join(", ")}
                    </span>
                    <span>
                      <strong>Max size:</strong> {formatBytes(headerMediaLimits.maxBytes)} (Meta limit)
                    </span>
                  </div>

                  {!headerMediaFile ? (
                    <label className="block">
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-5 text-center hover:border-emerald-400 transition-colors cursor-pointer bg-gray-50">
                        <Upload className="mx-auto mb-2 text-gray-400" size={28} />
                        <p className="text-sm font-bold text-gray-700 mb-1">
                          Click to upload {headerMediaLimits.label.toLowerCase()}
                        </p>
                        <p className="text-xs text-gray-500 font-medium">
                          {headerMediaLimits.extensions.join(", ")} •{" "}
                          up to {formatBytes(headerMediaLimits.maxBytes)}
                        </p>
                      </div>
                      <input
                        ref={headerMediaInputRef}
                        type="file"
                        accept={headerMediaLimits.accept}
                        onChange={handleHeaderMediaChange}
                        className="hidden"
                      />
                    </label>
                  ) : (
                    <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-3">
                      <div className="flex items-start gap-3">
                        {headerMediaFormat === "IMAGE" && headerMediaPreviewUrl ? (
                          <img
                            src={headerMediaPreviewUrl}
                            alt="Header preview"
                            className="w-16 h-16 rounded-lg object-cover border border-emerald-200 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0">
                            <Icon size={28} className="text-emerald-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate">
                            {headerMediaFile.name}
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5 font-medium">
                            {formatBytes(headerMediaFile.size)} •{" "}
                            {headerMediaFile.type || headerMediaLimits.label}
                          </p>
                          <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1 font-medium">
                            <CheckCircle size={12} />
                            Will be used for all contacts in this retarget
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveHeaderMedia}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                          title="Remove file"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  )}

                  {headerMediaError && (
                    <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-red-700 font-medium">{headerMediaError}</p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Campaign Name */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-sm font-bold text-gray-900 mb-3">Campaign Name *</h2>
              <input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                placeholder="Enter campaign name"
              />
            </div>

            {/* Send Options */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                {sendOption === "now" ? <Send size={16} /> : <Clock size={16} />}
                Send Options *
              </h2>
              <div className="space-y-3">
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="radio"
                      name="sendOption"
                      value="now"
                      checked={sendOption === "now"}
                      onChange={(e) => {
                        setSendOption(e.target.value);
                        setScheduledDate("");
                        setScheduledTime("");
                      }}
                      className="mr-2 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">
                      Send Now
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="radio"
                      name="sendOption"
                      value="schedule"
                      checked={sendOption === "schedule"}
                      onChange={(e) => setSendOption(e.target.value)}
                      className="mr-2 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">
                      Schedule
                    </span>
                  </label>
                </div>

                {sendOption === "schedule" && (
                  <div className="pt-3 border-t border-gray-200 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-2">
                          Scheduled Date *
                        </label>
                        <input
                          type="date"
                          value={scheduledDate}
                          onChange={(e) => setScheduledDate(e.target.value)}
                          min={moment().format("YYYY-MM-DD")}
                          className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-2">
                          Scheduled Time *
                        </label>
                        <input
                          type="time"
                          value={scheduledTime}
                          onChange={(e) => setScheduledTime(e.target.value)}
                          className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        Timezone
                      </label>
                      <input
                        type="text"
                        value={timezone}
                        readOnly
                        className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl bg-gray-100 font-medium"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/campaigns")}
                className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || loadingData || loadingTemplates}
                className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Retargeting...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Retarget Campaign
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="lg:sticky lg:top-6 h-fit">
            <div className="space-y-4">
              {selectedTemplates.length > 0 ? (
                <>
                  {/* Preview Navigation */}
                  {selectedTemplates.length > 1 && (
                    <div className="bg-white rounded-2xl border border-gray-200 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-gray-900">
                          Preview {activePreviewIndex + 1} of {selectedTemplates.length}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => navigatePreview('prev')}
                            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                          >
                            <ChevronLeft size={18} />
                          </button>
                          <button
                            onClick={() => navigatePreview('next')}
                            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                          >
                            <ChevronRight size={18} />
                          </button>
                        </div>
                      </div>
                      {(() => {
                        const template = validTemplates.find(
                          t => String(t.id).trim() === String(selectedTemplates[activePreviewIndex]).trim()
                        );
                        return template ? (
                          <p className="text-xs text-gray-600 mt-1 truncate font-medium">
                            {template.name}
                          </p>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {/* WhatsApp Preview */}
                  {(() => {
                    const templateId = selectedTemplates[activePreviewIndex];
                    const template = validTemplates.find(
                      t => String(t.id).trim() === String(templateId).trim()
                    );
                    if (!template) return null;
                    
                    const transformedTemplate = transformTemplateForPreview(template);
                    if (!transformedTemplate) return null;

                    // Get the selected phone account for preview
                    const selectedPhoneAccountForPreview = phoneAccounts.find(
                      p => p.phone_id === selectedPhoneId
                    );

                    // If the user uploaded a media file AND the template
                    // currently being previewed is the one whose format the
                    // upload matches, show the upload in the preview.
                    const previewTemplate =
                      headerMediaPreviewUrl &&
                      mediaHeaderTemplate?.template &&
                      String(mediaHeaderTemplate.template.id).trim() ===
                        String(template.id).trim()
                        ? { ...transformedTemplate, headerFilePreview: headerMediaPreviewUrl }
                        : transformedTemplate;

                    return (
                      <div>
                        {selectedPhoneAccountForPreview && (
                          <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                            <div className="flex items-center gap-2">
                              <Phone className="text-emerald-600" size={16} />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-emerald-900">
                                  Preview will use:
                                </p>
                                <p className="text-xs text-emerald-700 truncate">
                                  {selectedPhoneAccountForPreview.verified_name 
                                    ? `${selectedPhoneAccountForPreview.verified_name} - ${selectedPhoneAccountForPreview.phone_number}`
                                    : selectedPhoneAccountForPreview.phone_number}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        <WhatsAppPreview
                          templateData={previewTemplate}
                          defaultValues={defaultValues}
                          phoneAccount={selectedPhoneAccountForPreview ? {
                            phoneName: selectedPhoneAccountForPreview.verified_name || '',
                            phoneNumber: selectedPhoneAccountForPreview.phone_number || '',
                          } : null}
                        />
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="py-12 text-center">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 font-medium">
                      Select at least one template to see preview
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetargetCampaign;
